// Operation checklists: reading and writing (docs/op-checklists-plan.md), shared by the techs app
// (checklistTech.js) and the office (checklistOffice.js, Settings › Checklists). The rules are in
// checklistCore.js; this file only fetches and saves.
//
// Writes to an op go through trackedDb (a tick made with no signal is kept and sent later), and the
// page's own copy is updated at once through the page's mirror(). A list is applied automatically
// the first time an op is opened: online that's a transaction, so two people can't set two lists.
// Templates and stabilizers are small and listened to; a boat's lists are read when one of its ops
// opens. A copy of each stays in localStorage for opening an op with no signal.

import { ref, get, set, update, remove, onValue, runTransaction } from 'firebase/database';
import { database, isFirebaseConnected } from './initFirebase';
import { update as dbUpdate, set as dbSet } from './trackedDb';
import * as C from './checklistCore';
import * as B from './boatDataCore';
import { saveFact as saveBoatFact } from './boatDataData';

// The page's hooks: who is saving, whether a write can reach the server now, and how to update
// the page's own copy of an op (mirror(opId, pathUpdates)).
const ctx = {
  by: () => '',
  canSend: () => navigator.onLine && isFirebaseConnected(),
  mirror: () => {},
  onSaveError: () => {},
};
export function configureChecklists(hooks) { Object.assign(ctx, hooks); }

// ─── Copies ───

function readCopy(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function writeCopy(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full */ }
}

let templates = readCopy('clTemplates:v1', {});
let stabilizers = readCopy('clStabilizers:v1', {});
const boatLists = new Map(); // boatId → { code: list }
const boatKey = id => `clBoat:v1:${id}`;

export const getTemplates = () => templates;
export const getStabilizers = () => stabilizers;

export function listsFor(boatId) {
  if (!boatId) return {};
  if (!boatLists.has(boatId)) boatLists.set(boatId, readCopy(boatKey(boatId), {}));
  return boatLists.get(boatId) || {};
}
function setBoatListCopy(boatId, code, list) {
  const all = { ...listsFor(boatId) };
  if (list) all[code] = list; else delete all[code];
  boatLists.set(boatId, all);
  writeCopy(boatKey(boatId), all);
}

const listeners = new Set();
let watching = false;
/** Listen to templates and stabilizers (once per page); onChange runs after either changes. */
export function watchChecklistSettings(onChange) {
  if (onChange) listeners.add(onChange);
  if (watching) return () => listeners.delete(onChange);
  watching = true;
  const changed = (what) => listeners.forEach(fn => { try { fn(what); } catch (err) { console.error(err); } });
  onValue(ref(database, 'checklists/templates'), (s) => {
    templates = s.val() || {};
    writeCopy('clTemplates:v1', templates);
    changed('templates');
  }, err => console.warn('Checklist templates unavailable', err));
  onValue(ref(database, 'settings/stabilizers'), (s) => {
    stabilizers = s.val() || {};
    writeCopy('clStabilizers:v1', stabilizers);
    changed('stabilizers');
  }, err => console.warn('Stabilizers unavailable', err));
  return () => listeners.delete(onChange);
}

function timeout(p, ms = 6000) {
  let t;
  return Promise.race([p, new Promise((_, rej) => { t = setTimeout(() => rej(new Error('No answer from the server')), ms); })]).finally(() => clearTimeout(t));
}

const fetched = new Map(); // boatId → when it was last read from the server
/** A boat's lists ({ code: list }): from the server when there's a signal and the copy is older than maxAgeMs. */
export async function loadBoatLists(boatId, maxAgeMs = 60000) {
  if (!boatId || !ctx.canSend() || Date.now() - (fetched.get(boatId) || 0) < maxAgeMs) return listsFor(boatId);
  try {
    const v = (await timeout(get(ref(database, `checklists/boats/${boatId}`)))).val() || {};
    fetched.set(boatId, Date.now());
    boatLists.set(boatId, v);
    writeCopy(boatKey(boatId), v);
  } catch (err) {
    console.warn('Checklist: could not read the boat’s lists, using the copy', err);
  }
  return listsFor(boatId);
}

/** Read several boats' lists while there's a signal, so an op opened offline later still gets the right one. */
export function prefetchBoatLists(boatIds) {
  [...new Set(boatIds)].filter(Boolean).forEach(id => loadBoatLists(id, 30 * 60000));
}

/** What could go on this op: chooseList()'s options. */
export function listOptions(op, boat) {
  return C.chooseList({ op, boat, boatLists: listsFor(op.Boat_ID), templates }).options;
}

// ─── Writes to an op ───

const stamp = () => ({ by: ctx.by(), at: Date.now() });

export function writeOp(op, updates) {
  ctx.mirror(op.id || op.Job_ID, updates);
  return dbUpdate(ref(database, `data/${op.id || op.Job_ID}`), updates).catch((err) => {
    console.error('Checklist: save failed', err);
    ctx.onSaveError(err);
  });
}

/** Keep (or drop) one item on the boat's list for this op code. The first change makes the list from the op. */
function syncBoat(op, iid, { remove: drop = false } = {}) {
  const code = C.normCode(op.OpCode);
  if (!code || !op.Boat_ID) return;
  const { by, at } = stamp();
  const own = listsFor(op.Boat_ID)[code];
  if (own) {
    const item = op.Checklist?.Items?.[iid];
    const updates = { [`Items/${iid}`]: drop || !item ? null : C.cleanItem(item), Updated_At: at, Updated_By: by, From_Op: op.id || op.Job_ID };
    setBoatListCopy(op.Boat_ID, code, C.applyPaths(JSON.parse(JSON.stringify(own)), updates));
    dbUpdate(ref(database, `checklists/boats/${op.Boat_ID}/${code}`), updates).catch(err => console.error('Checklist: boat list save failed', err));
    return;
  }
  replaceBoatList(op, C.boatListFromOp(op.Checklist));
}

function replaceBoatList(op, list) {
  const code = C.normCode(op.OpCode);
  if (!code || !op.Boat_ID) return;
  const { by, at } = stamp();
  const value = list && Object.keys(list.Items || {}).length ? { ...list, Updated_At: at, Updated_By: by, From_Op: op.id || op.Job_ID } : null;
  setBoatListCopy(op.Boat_ID, code, value);
  dbSet(ref(database, `checklists/boats/${op.Boat_ID}/${code}`), value).catch(err => console.error('Checklist: boat list save failed', err));
}

/** Finish: a boat with no list of its own for this op code keeps what was used. */
export function keepListOnFinish(op) {
  const code = C.normCode(op.OpCode);
  if (!code || !op.Boat_ID || !C.liveItems(op.Checklist).length || listsFor(op.Boat_ID)[code]) return;
  replaceBoatList(op, C.boatListFromOp(op.Checklist));
}

const FINISHED = ['Ready to Bill', 'Complete', 'Closed', 'Declined'];
const applying = new Set();
/** The first time an op is opened: put the best list on it. Resolves true when the op's copy changed. */
export async function ensureList(op, boat) {
  const id = op.id || op.Job_ID;
  // Opening a finished op (to bill it, or from history) never puts a list on it.
  if (op.Checklist || !C.normCode(op.OpCode) || applying.has(id) || FINISHED.includes(String(op.Status || ''))) return false;
  applying.add(id);
  try {
    const lists = await loadBoatLists(op.Boat_ID, 0);
    if (op.Checklist) return false;
    const { auto } = C.chooseList({ op, boat, boatLists: lists, templates });
    if (!auto) return false;
    const snap = C.snapshot(auto, { by: ctx.by(), at: Date.now(), boat });
    if (ctx.canSend()) {
      const res = await timeout(runTransaction(ref(database, `data/${id}/Checklist`), cur => (cur == null ? snap : undefined)), 10000);
      const applied = res.snapshot.val(); // someone else's, if they got there first
      if (applied) ctx.mirror(id, { Checklist: applied });
    } else {
      // No signal: one field per item, so it lines up with whoever sets the same list first.
      const updates = { 'Checklist/Source': snap.Source, 'Checklist/Set_By': snap.Set_By, 'Checklist/Set_At': snap.Set_At };
      if (snap.Engine_Type) updates['Checklist/Engine_Type'] = snap.Engine_Type;
      if (snap.Engines) updates['Checklist/Engines'] = snap.Engines;
      Object.entries(snap.Items).forEach(([iid, it]) => { updates[`Checklist/Items/${iid}`] = it; });
      writeOp(op, updates);
    }
    return true;
  } catch (err) {
    console.warn('Checklist: could not apply a list', err);
    return false;
  } finally {
    applying.delete(id);
  }
}

/** Tick or untick one box (key = engine key or 'boat'). */
export function toggleDone(op, iid, key) {
  const cur = op.Checklist?.Done?.[iid]?.[key];
  const { by, at } = stamp();
  return writeOp(op, { [`Checklist/Done/${iid}/${key}`]: cur ? null : { By: by, At: at } });
}

/**
 * A reading ('' clears it). Engine hours also go on the engine, and a reading that names a boat
 * field (item.Boat_Field) goes on the boat. Returns false when it isn't a number; notice(en, es)
 * says when it was kept on the op but couldn't go on the boat.
 */
export function saveReading(op, iid, key, raw, { boat = null, mirrorBoat = () => {}, notice = () => {} } = {}) {
  const v = String(raw || '').trim().replace(',', '.');
  if (v && !/^-?\d+(\.\d+)?$/.test(v)) return false;
  const prev = op.Checklist?.Done?.[iid]?.[key]?.Value ?? '';
  if (String(prev) === v) return true;
  const { by, at } = stamp();
  writeOp(op, { [`Checklist/Done/${iid}/${key}`]: v ? { By: by, At: at, Value: v } : null });
  const item = op.Checklist?.Items?.[iid];
  if (v && item?.Engine_Hours && boat?.Engines?.[key] && op.Customer_ID && op.Boat_ID) {
    mirrorBoat({ [`Engines/${key}/Engine_Hours`]: v });
    dbUpdate(ref(database, `customers/${op.Customer_ID}/Boats/${op.Boat_ID}/Engines/${key}`), { Engine_Hours: v })
      .catch(err => console.error('Checklist: engine hours not saved on the boat', err));
  }
  if (v && item?.Boat_Field && boat && op.Customer_ID && op.Boat_ID) saveBoatField(op, item.Boat_Field, v, { boat, mirrorBoat, notice });
  return true;
}

// The boat-field part of a reading: checked like the Boat info card's answers (feet and inches,
// typo ranges); a total fuel capacity is split over the boat's tanks when it can be.
function saveBoatField(op, field, v, { boat, mirrorBoat, notice }) {
  const target = { customerId: op.Customer_ID, boatId: op.Boat_ID };
  const by = ctx.by();
  const res = B.parseAnswer(field === 'Fuel_Capacity' ? 'Capacity_Gal' : field, v);
  if (res.error) { notice(`Kept on the checklist, not on the boat: ${res.error.EN}`, `Guardado en la lista, no en el barco: ${res.error.ES}`); return; }
  if (String(B.checklistFieldValue(boat, field)) === String(res.value)) return; // the same as on file
  if (field !== 'Fuel_Capacity') { saveBoatFact(target, field, res.value, { by, mirror: mirrorBoat }).catch(err => console.error('Checklist: boat field not saved', err)); return; }
  const updates = B.capacityUpdates(boat, res.value, { By: by, At: Date.now() });
  if (!updates) {
    const n = B.tanksOf(boat, 'fuel').length;
    notice(`This boat has ${n} fuel tanks: fill in each one's size on the boat`, `Este barco tiene ${n} tanques: anota el tamaño de cada uno en el barco`);
    return;
  }
  mirrorBoat(updates);
  dbUpdate(ref(database, `customers/${op.Customer_ID}/Boats/${op.Boat_ID}`), updates).catch(err => console.error('Checklist: fuel capacity not saved on the boat', err));
}

/** The fuel gauge reading on the stabilizer step, with the dose it works out to ('' clears it). */
export function saveStabilizerGauge(op, gauge, dose) {
  const { by, at } = stamp();
  return writeOp(op, { 'Checklist/Stab_Gauge': gauge ? { Gauge: gauge, By: by, At: at, ...(dose ? { Dose: `${dose.amount} ${dose.unit}`, Fuel_Gal: dose.fuelGal } : {}) } : null });
}

/** The boat's fuel type, picked from the stabilizer line. */
export function saveFuelType(op, fuel, { mirrorBoat = () => {} } = {}) {
  if (!op.Customer_ID || !op.Boat_ID) return;
  mirrorBoat({ Fuel_Type: fuel });
  dbUpdate(ref(database, `customers/${op.Customer_ID}/Boats/${op.Boat_ID}`), { Fuel_Type: fuel })
    .catch(err => console.error('Checklist: fuel type not saved', err));
}

/** Add an item to the op. keep: also on the boat's list for next time. Returns the new id. */
export function addItem(op, { name, kind = 'check', unit = '', scope = 'engine', keep = true }) {
  const cl = op.Checklist || {};
  const { by, at } = stamp();
  const iid = C.newItemId();
  const item = { ...C.cleanItem({ Name: name, Kind: kind, Unit: unit, Scope: scope }, C.nextOrder(cl.Items)), Added_By: by, Added_At: at };
  if (!keep) item.Once = true;
  const updates = { [`Checklist/Items/${iid}`]: item };
  if (!cl.Source || cl.Source.kind === 'none') Object.assign(updates, { 'Checklist/Source': { kind: 'new' }, 'Checklist/Set_By': by, 'Checklist/Set_At': at });
  writeOp(op, updates);
  if (keep) syncBoat(op, iid);
  return iid;
}

/** Take an item off the op (it stays, marked, so the office sees it was taken off). keep: off the boat's list too. */
export function removeItem(op, iid, { keep = true } = {}) {
  const { by, at } = stamp();
  writeOp(op, {
    [`Checklist/Items/${iid}/Removed_At`]: at, [`Checklist/Items/${iid}/Removed_By`]: by,
    [`Checklist/Items/${iid}/Removed_Once`]: keep ? null : true,
  });
  if (keep) syncBoat(op, iid, { remove: true });
}

export function restoreItem(op, iid) {
  const it = op.Checklist?.Items?.[iid];
  if (!it) return;
  writeOp(op, { [`Checklist/Items/${iid}/Removed_At`]: null, [`Checklist/Items/${iid}/Removed_By`]: null, [`Checklist/Items/${iid}/Removed_Once`]: null });
  if (!it.Once) syncBoat(op, iid);
}

/** Put another list on the op (opt from listOptions()), or none. keep: the boat remembers the choice. */
export function switchList(op, opt, { keep = true, boat = null } = {}) {
  const code = C.normCode(op.OpCode);
  const list = opt ? C.listFor(opt, { boatLists: listsFor(op.Boat_ID), templates, code }) : null;
  const snap = C.snapshot(list, { by: ctx.by(), at: Date.now(), boat });
  writeOp(op, { Checklist: snap });
  if (keep) {
    if (!list) replaceBoatList(op, null);
    else if (opt.kind !== 'boat') replaceBoatList(op, C.boatListFromOp(snap));
  }
  return snap;
}

export function saveOpenReason(op, text) {
  if (text === (op.Checklist?.Open_Reason || '')) return;
  writeOp(op, { 'Checklist/Open_Reason': text || null });
}

// ─── The office's own records (online; Settings › Checklists and the boat page) ───

export function saveTemplate(tid, template) {
  const { by, at } = stamp();
  return set(ref(database, `checklists/templates/${tid}`), { ...template, Updated_At: at, Updated_By: by });
}
/** Offer a saved list for one more op code from now on (picked for an op of that code). */
export function addTemplateCode(tid, code) {
  const c = C.templateCode(code);
  if (!c || !templates[tid]) return Promise.resolve(false);
  const { by, at } = stamp();
  templates = { ...templates, [tid]: { ...templates[tid], OpCodes: { ...templates[tid].OpCodes, [c]: true } } };
  return update(ref(database, `checklists/templates/${tid}`), { [`OpCodes/${c}`]: true, Updated_At: at, Updated_By: by }).then(() => true);
}
export const deleteTemplate = tid => remove(ref(database, `checklists/templates/${tid}`));
export const saveStabilizer = (fuel, value) => set(ref(database, `settings/stabilizers/${fuel}`), value && (value.Name || value.Dose) ? value : null);

/** A boat's saved list: drop one item, or the whole list (iid null). */
export async function editBoatList(boatId, code, { dropItem = null } = {}) {
  const path = `checklists/boats/${boatId}/${code}`;
  if (dropItem) {
    const { by, at } = stamp();
    await update(ref(database, path), { [`Items/${dropItem}`]: null, Updated_At: at, Updated_By: by });
  } else {
    await remove(ref(database, path));
  }
  fetched.delete(boatId);
  return loadBoatLists(boatId, 0);
}
